import React, { useState, createContext, useCallback, useContext, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HelmetProvider } from 'react-helmet-async';

// Import contexts
import { UserProvider } from './contexts/UserContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Import API utility
import api from './utils/api';

// Core components (always loaded - needed on every page)
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import ErrorBoundary from './components/ErrorBoundary';
import AsyncErrorBoundary from './components/AsyncErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import { useUser } from './contexts/UserContext';
import logger from './utils/logger';

// Lazy-loaded components (split into separate chunks)
const UploadPage = lazy(() => import('./components/UploadPage'));
const HistoryPage = lazy(() => import('./components/HistoryPage'));
const SessionDetailsPage = lazy(() => import('./components/SessionDetailsPage'));
const AccountPage = lazy(() => import('./components/AccountPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const AdminVerifyPage = lazy(() => import('./components/AdminVerifyPage'));
const RevenueDashboard = lazy(() => import('./components/RevenueDashboard'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const ForgotPasswordPage = lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const LoRATrainingPage = lazy(() => import('./components/LoRATrainingPage'));

// Legal pages (rarely visited)
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const CookiePolicy = lazy(() => import('./components/CookiePolicy'));

// Blog components
const BlogList = lazy(() => import('./components/blog/BlogList'));
const BlogPost = lazy(() => import('./components/blog/BlogPost'));
const BlogAdmin = lazy(() => import('./components/blog/BlogAdmin'));
const BlogEditor = lazy(() => import('./components/blog/BlogEditor'));

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Create context for global state
export const AppContext = createContext();

function AppContent() {
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const { isDarkMode } = useTheme();
  
  const navigate = useNavigate();

  // Create new session
  const createNewSession = useCallback(async (name = '', additionalData = {}) => {
    try {
      const data = await api.sessions.create({ 
        name,
        ...additionalData
      });
      
      logger.debug('Create session response:', data);
      
      // Handle standardized success response format
      if (data.success && data.data) {
        const responseData = data.data;
        const sessionId = responseData.sessionId;
        const session = responseData.session || {};
        
        if (!sessionId) {
          logger.error('No session ID found in response:', data);
          throw new Error('Invalid session response - no ID found');
        }
        
        // Return the session data with the ID
        return { ...session, id: sessionId };
      }
      
      // Fallback for non-standardized response (shouldn't happen)
      const sessionId = data.sessionId || data.id;
      if (!sessionId) {
        logger.error('No session ID found in response:', data);
        throw new Error('Invalid session response - no ID found');
      }
      
      return { ...data, id: sessionId };
    } catch (error) {
      logger.error('Error creating session:', error);
      toast.error('Failed to create new session');
      return null;
    }
  }, []);

  // Update session status
  const updateSessionStatus = useCallback(async (sessionId, status) => {
    try {
      const updatedSession = await api.sessions.updateStatus(sessionId, status);
      return updatedSession;
    } catch (error) {
      logger.error('Error updating session status:', error);
      toast.error('Failed to update session status');
      return null;
    }
  }, []);

  // Complete session
  const completeSession = useCallback(async (sessionId) => {
    try {
      await api.sessions.complete(sessionId);
      
      toast.success('Session completed successfully');
      navigate('/history');
    } catch (error) {
      logger.error('Error completing session:', error);
      toast.error('Failed to complete session');
    }
  }, [navigate]);

  // Context value
  const contextValue = {
    isLoading,
    setIsLoading,
    createNewSession,
    updateSessionStatus,
    completeSession
  };

  return (
    <UserProvider>
      <AppContext.Provider value={contextValue}>
        <ErrorBoundary>
          <div className="min-h-screen bg-background transition-colors">
            <Header />
          
          <main className="container mx-auto px-4 py-2">
            <AsyncErrorBoundary>
              <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/history" element={
                <ProtectedRoute>
                  <HistoryPage />
                </ProtectedRoute>
              } />
              <Route path="/session/:sessionId" element={
                <ProtectedRoute>
                  <SessionDetailsPage />
                </ProtectedRoute>
              } />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/admin" element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/verify" element={<AdminVerifyPage />} />
              <Route path="/admin/revenue" element={
                <ProtectedRoute requireAdmin={true}>
                  <RevenueDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/analytics" element={
                <ProtectedRoute requireAdmin={true}>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              } />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/lora-training" element={<LoRATrainingPage />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/admin" element={
                <ProtectedRoute requireAdmin={true}>
                  <BlogAdmin />
                </ProtectedRoute>
              } />
              <Route path="/blog/admin/new" element={
                <ProtectedRoute requireAdmin={true}>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="/blog/admin/edit/:id" element={
                <ProtectedRoute requireAdmin={true}>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
            </AsyncErrorBoundary>
          </main>
          
          <Footer />
          
          {/* Cookie consent banner */}
          <CookieConsent />
          
          {/* Toast notifications */}
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={isDarkMode ? "dark" : "light"}
          />
        </div>
      </ErrorBoundary>
      </AppContext.Provider>
    </UserProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;